import {NotificationsSubscriptionsMenu} from '@github-ui/notifications-subscriptions-menu'
import {RepoTemplateIcon} from '@primer/octicons-react'
import {PinButton, shouldShowPinButton} from '@github-ui/repo-pin-button'
import type {PinButtonProps} from '@github-ui/repo-pin-button/types'
import {StarButton} from '@github-ui/repo-star-button'
import {Button} from '@primer/react'

import type {HeaderResponse, RepoPaths} from '../types'
import {ForkButton} from '@github-ui/repo-fork-button'

export type CodeViewHeaderActionsProps = Pick<
  HeaderResponse,
  | 'repo'
  | 'forksCount'
  | 'stargazerCount'
  | 'showSponsorButton'
  | 'showTemplateButton'
  | 'star'
  | 'fork'
  | 'pin'
  | 'watch'
  | 'viewer'
> & {
  paths: RepoPaths
}

/** Desktop action button container with Watch, Fork, and Star buttons. */
export function CodeViewHeaderActions({
  repo,
  pin,
  watch,
  viewer,
  paths,
  star,
  stargazerCount,
  fork,
  forksCount,
  showTemplateButton,
}: CodeViewHeaderActionsProps) {
  const pinProps: PinButtonProps = {
    repoNameWithOwner: paths.repoNameWithOwner,
    repoUrl: paths.repoUrl,
    isPrivate: repo.isPrivate,
    isOrgOwned: pin.isOrgOwned,
    canPin: pin.canPin,
    isPinned: pin.isPinned,
    isLoggedIn: viewer.isLoggedIn,
    pinItemsRemaining: pin.pinItemsRemaining,
    orgPinData: pin.orgPinData,
  }

  return (
    <ul className="list-style-none d-flex flex-items-center gap-2 flex-shrink-0 py-1" data-testid="repo-header-actions">
      {shouldShowPinButton(pinProps) && (
        <li>
          <PinButton
            {...pinProps}
            // TODO: Wire onError to CodeViewBannersContext only renders inside TreeBanners/BlobBanners — not in the header area.
            // Need to either add a banner render point near the header or use the toast system.
            onError={_error => {
              // TODO: Show error banner or toast to the user
            }}
          />
        </li>
      )}

      {viewer.isLoggedIn && watch.canWatch && (
        <li>
          <NotificationsSubscriptionsMenu
            repositoryId={watch.watchData.repositoryId}
            repositoryName={watch.watchData.repositoryName}
            watchersCount={watch.watchData.watchersCount}
            subscriptionType={watch.watchData.subscriptionType}
            subscribableThreadTypes={watch.watchData.subscribableThreadTypes}
            repositoryLabels={watch.watchData.repositoryLabels}
            showLabelSubscriptions={watch.watchData.showLabelSubscriptions}
            subscribedLabelIds={watch.watchData.subscribedLabelIds}
            subscribedLabels={[]}
          />
        </li>
      )}

      <li>
        <ForkButton
          forkCount={forksCount}
          canFork={fork.canFork}
          forkabilityError={fork.forkabilityError}
          repoNameWithOwner={paths.repoNameWithOwner}
          forkSelectPath={paths.forkSelectPath}
          forksApiPath={paths.forksApiPath}
          isLoggedIn={viewer.isLoggedIn}
        />
      </li>

      <li>
        <StarButton
          starred={star.viewerHasStarred}
          starCount={stargazerCount}
          starPath={paths.starPath}
          unstarPath={paths.unstarPath}
          canStar={star.canStar}
          isLoggedIn={viewer.isLoggedIn}
          listsApiPath={paths.listsApiPath}
          listsUpdatePath={paths.listsUpdatePath}
          repoNameWithOwner={paths.repoNameWithOwner}
          emuContributionBlocked={viewer.emuContributionBlocked}
        />
      </li>

      {showTemplateButton && repo.isTemplate && (
        <li>
          <Button
            as="a"
            href={paths.templatePath}
            variant="primary"
            size="small"
            leadingVisual={RepoTemplateIcon}
            data-testid="use-template-button"
          >
            Use this template
          </Button>
        </li>
      )}
    </ul>
  )
}
